import React from 'react';
import styled from 'styled-components';
import useTheme from 'hooks/useTheme';
import {
	BillingIcon,
	CheckCircleIcon,
	OrderIcon,
	WarehouseIcon,
} from 'components';
import WarrantyRestriction from '../WarrantyRestriction';
import InventoryCompletionException from '../InventoryCompletionException';
import {
	MaterialValidationControl,
	useMaterialValidation,
} from '../MaterialValidation';
import { getMaterialValidationPresentation } from '../MaterialValidation/materialValidationPresentation';
import NoPayRestriction from '../NoPayRestriction';
import BillingRevertAction from '../BillingReversal';
import { findWarrantyField } from '../restrictionUtils';

export const getRestrictionsThemeTokens = theme => ({
	background: theme._primaryBackgroundColor,
	surface: theme._secondaryBackgroundColor,
	elevatedSurface: theme._surfaceBackgroundColor,
	border: theme._borderColor,
	textPrimary: theme._highEmphasisColor,
	textSecondary: theme._mediumEmphasisColor,
	inactive: theme._inactiveColor,
	accent: theme._secondaryColor,
	warning: theme._warningColor,
});

const toneColor = ({ $tone }) => {
	if ($tone === 'warning') return 'var(--restrictions-warning)';
	if ($tone === 'success') return 'var(--restrictions-accent)';
	return 'var(--restrictions-inactive)';
};

const Restrictions = styled.div`
	--restrictions-background: ${({ $tokens }) => $tokens.background};
	--restrictions-surface: ${({ $tokens }) => $tokens.surface};
	--restrictions-surface-raised: ${({ $tokens }) => $tokens.elevatedSurface};
	--restrictions-border: ${({ $tokens }) => $tokens.border};
	--restrictions-text: ${({ $tokens }) => $tokens.textPrimary};
	--restrictions-text-secondary: ${({ $tokens }) => $tokens.textSecondary};
	--restrictions-inactive: ${({ $tokens }) => $tokens.inactive};
	--restrictions-accent: ${({ $tokens }) => $tokens.accent};
	--restrictions-warning: ${({ $tokens }) => $tokens.warning};

	box-sizing: border-box;
	display: flex;
	flex-direction: column;
	gap: 12px;
	height: calc(100vh - 65px);
	overflow-y: auto;
	padding: 14px 6px 24px;
	width: 100%;

	@media (max-width: 768px) {
		padding: 12px 6px 20px;
	}

	@media (max-width: 390px) {
		gap: 10px;
		padding: 10px 4px 18px;
	}
`;

const Overview = styled.header`
	align-items: center;
	background: var(--restrictions-surface);
	border: 1px solid var(--restrictions-border);
	border-left: 4px solid var(--restrictions-accent);
	border-radius: 10px;
	display: flex;
	gap: 12px;
	justify-content: space-between;
	padding: 13px 15px;

	@media (max-width: 390px) {
		align-items: flex-start;
		flex-direction: column;
	}
`;

const OverviewLead = styled.div`
	align-items: center;
	display: flex;
	gap: 11px;
	min-width: 0;
`;

const OverviewIcon = styled.div`
	align-items: center;
	background: var(--restrictions-surface-raised);
	border: 1px solid var(--restrictions-border);
	border-radius: 8px;
	display: flex;
	flex: 0 0 auto;
	height: 38px;
	justify-content: center;
	width: 38px;
`;

const OverviewText = styled.div`
	min-width: 0;
`;

const OverviewTitle = styled.h1`
	color: var(--restrictions-text);
	font-size: 16px;
	font-weight: 750;
	line-height: 1.3;
	margin: 0;
`;

const OverviewDescription = styled.p`
	color: var(--restrictions-text-secondary);
	font-size: 12px;
	line-height: 1.45;
	margin: 3px 0 0;
`;

const CountBadge = styled.span`
	border: 1px solid var(--restrictions-border);
	border-radius: 999px;
	color: var(--restrictions-text-secondary);
	flex: 0 0 auto;
	font-size: 11px;
	font-weight: 700;
	line-height: 1;
	padding: 6px 9px;
	text-transform: uppercase;
`;

const ControlList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;

	@media (max-width: 390px) {
		gap: 10px;
	}
`;

const Restriction = styled.section`
	background: var(--restrictions-surface);
	border: 1px solid var(--restrictions-border);
	border-left: 3px solid ${toneColor};
	border-radius: 9px;
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
	min-height: 0;
	min-width: 0;
	padding: 13px 14px 14px;
	width: 100%;

	&:focus-within {
		border-color: var(--restrictions-accent);
		outline: 2px solid var(--restrictions-accent);
		outline-offset: 2px;
	}

	@media (max-width: 390px) {
		min-height: 0;
		padding: 12px;
	}
`;

const Header = styled.div`
	align-items: flex-start;
	display: flex;
	flex-wrap: wrap;
	gap: 10px;
	justify-content: space-between;
`;

const TitleGroup = styled.div`
	align-items: center;
	display: flex;
	gap: 9px;
	min-width: 0;
`;

const CardIcon = styled.div`
	align-items: center;
	display: flex;
	flex: 0 0 auto;
	height: 24px;
	justify-content: center;
	width: 24px;
`;

const Heading = styled.h2`
	color: var(--restrictions-text);
	font-size: 14px;
	font-weight: 750;
	line-height: 1.35;
	margin: 0;
`;

const StatusChip = styled.span`
	border: 1px solid ${toneColor};
	border-radius: 999px;
	color: var(--restrictions-text);
	flex: 0 0 auto;
	font-size: 10px;
	font-weight: 750;
	line-height: 1;
	padding: 5px 7px;
	text-align: center;
	text-transform: uppercase;
	white-space: normal;
`;

const Description = styled.p`
	color: var(--restrictions-text-secondary);
	font-size: 12px;
	line-height: 1.45;
	margin: 7px 0 11px;
`;

const Content = styled.div`
	align-items: stretch;
	display: flex;
	flex: 1;
	justify-content: flex-start;
	min-height: 38px;
	min-width: 0;
	width: 100%;

	> * {
		max-width: 100%;
	}
`;

const StateHint = styled.span`
	color: var(--restrictions-text-secondary);
	font-size: 12px;
	font-style: italic;
`;

export const getWarrantyPresentation = (data, isFetching = false) => {
	if (isFetching) return { label: 'Actualizando', tone: 'neutral' };
	const field = findWarrantyField(data);
	if (!field) return { label: 'No disponible', tone: 'neutral' };
	return field.value === true
		? { label: 'Bloquea pago', tone: 'warning' }
		: { label: 'Sin garantía', tone: 'success' };
};

export const getMaterialPresentation = getMaterialValidationPresentation;

const sameOrderId = (left, right) =>
	left !== undefined &&
	left !== null &&
	right !== undefined &&
	right !== null &&
	String(left) === String(right);

export const getBillingPresentation = (billing, orderId) => {
	if (!sameOrderId(billing?.orderId, orderId))
		return { label: 'Consultando', tone: 'neutral' };
	if (billing?.isFetching) return { label: 'Consultando', tone: 'neutral' };
	if (billing?.error) return { label: 'No confirmada', tone: 'warning' };
	if (sameOrderId(billing?.transactionDoc?.order_id, orderId))
		return { label: 'Facturada', tone: 'success' };
	if (billing?.transactionDoc?.order_id)
		return { label: 'No confirmada', tone: 'warning' };
	return { label: 'Sin facturación', tone: 'neutral' };
};

const getStatusChipLabel = status =>
	status?.chipLabel || status?.label?.split(' · ')[0] || '';

const RestrictionCard = ({
	id,
	title,
	description,
	icon,
	status,
	children,
}) => (
	<Restriction
		aria-labelledby={id}
		$tone={status?.tone || 'neutral'}
		data-restriction-card={id}
		data-tone={status?.tone || 'neutral'}
	>
		<Header>
			<TitleGroup>
				<CardIcon aria-hidden='true'>{icon}</CardIcon>
				<Heading id={id}>{title}</Heading>
			</TitleGroup>
			{status && (
				<StatusChip
					$tone={status.tone}
					data-status-chip
					role='status'
					aria-live='polite'
					aria-label={status.label}
				>
					{getStatusChipLabel(status)}
				</StatusChip>
			)}
		</Header>
		<Description>{description}</Description>
		<Content role='group' aria-labelledby={id}>
			{children}
		</Content>
	</Restriction>
);

export default function RestrictionsTab({
	orderId,
	data,
	metadata,
	billing,
	isActive,
	isFetching,
	onChanged,
}) {
	const { theme, themeProfile } = useTheme();
	const themeTokens = getRestrictionsThemeTokens(theme);
	const notifyChanged = () => {
		if (onChanged) onChanged(orderId);
	};
	const materialValidation = useMaterialValidation(orderId, {
		onChanged: notifyChanged,
		isActive,
	});
	const warrantyStatus = getWarrantyPresentation(data, isFetching);
	const materialStatus = getMaterialPresentation(materialValidation);
	const billingMatchesOrder = sameOrderId(billing?.orderId, orderId);
	const billedOrderMatches = sameOrderId(
		billing?.transactionDoc?.order_id,
		orderId,
	);
	const billingStatus = getBillingPresentation(billing, orderId);
	const controls = [
		<RestrictionCard
			key='warranty'
			id='order-restriction-warranty-title'
			title='Garantía'
			description='Define si la orden debe excluirse del pago por garantía.'
			icon={<CheckCircleIcon active={warrantyStatus.tone === 'success'} />}
			status={warrantyStatus}
		>
			<WarrantyRestriction
				orderId={orderId}
				data={data}
				metadata={metadata}
				isFetching={isFetching}
				onChanged={notifyChanged}
			/>
		</RestrictionCard>,
		<RestrictionCard
			key='no-pay'
			id='order-restriction-no-pay-title'
			title='Código no pagable'
			description='Revise el código de cierre y autorice el pago cuando corresponda.'
			icon={<BillingIcon size='normal' />}
		>
			<NoPayRestriction orderId={orderId} onChanged={notifyChanged} />
		</RestrictionCard>,
		<RestrictionCard
			key='billing'
			id='order-restriction-billing-title'
			title='Facturación'
			description='Revierte la facturación generada para recalcular la orden cuando corresponda.'
			icon={<BillingIcon size='normal' />}
			status={billingStatus}
		>
			{!billingMatchesOrder || billing?.isFetching ? (
				<StateHint>Consultando facturación…</StateHint>
			) : billing?.error ? (
				<StateHint role='alert'>No se pudo confirmar la facturación</StateHint>
			) : billedOrderMatches ? (
				<BillingRevertAction
					orderId={billing.transactionDoc.order_id}
					onChanged={notifyChanged}
				/>
			) : billing?.transactionDoc?.order_id ? (
				<StateHint role='alert'>
					La facturación no corresponde a la orden visible
				</StateHint>
			) : (
				<StateHint>Sin facturación generada</StateHint>
			)}
		</RestrictionCard>,
		<RestrictionCard
			key='required-materials'
			id='order-restriction-required-materials-title'
			title='Materiales para completar'
			description='Gestione la excepción para completar una orden sin materiales.'
			icon={<WarehouseIcon size='normal' />}
			status={{ label: 'Reglas de cierre', tone: 'neutral' }}
		>
			<InventoryCompletionException
				orderId={orderId}
				isActive={isActive}
				onChanged={notifyChanged}
			/>
		</RestrictionCard>,
		<RestrictionCard
			key='material-validation'
			id='order-restriction-material-validation-title'
			title='Validación de materiales'
			description='Revise el caso vigente y decida según la evidencia reportada.'
			icon={<WarehouseIcon size='normal' />}
			status={materialStatus}
		>
			<MaterialValidationControl
				key={orderId}
				{...materialValidation}
				onRetry={materialValidation.refresh}
				onRefreshEvidence={materialValidation.refresh}
				onDecide={materialValidation.decide}
				onRequestManualCorrection={materialValidation.requestManualCorrection}
				onRetryManualCorrectionRefresh={
					materialValidation.retryManualCorrectionRefresh
				}
				onRefreshManualAdjustmentState={
					materialValidation.refreshManualAdjustmentState
				}
			/>
		</RestrictionCard>,
	];

	return (
		<Restrictions
			aria-label='Restricciones de la orden'
			data-layout='restriction-dashboard'
			data-theme={themeProfile}
			$tokens={themeTokens}
		>
			<Overview>
				<OverviewLead>
					<OverviewIcon aria-hidden='true'>
						<OrderIcon size='normal' />
					</OverviewIcon>
					<OverviewText>
						<OverviewTitle>Validaciones de la orden</OverviewTitle>
						<OverviewDescription>
							Identifique bloqueos y gestione excepciones desde un solo lugar.
						</OverviewDescription>
					</OverviewText>
				</OverviewLead>
				<CountBadge data-control-count>{controls.length} controles</CountBadge>
			</Overview>

			<ControlList data-testid='restrictions-list' data-layout='vertical'>
				{controls}
			</ControlList>
		</Restrictions>
	);
}
